import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useAuthentication } from "@/hooks/useAuthentication";
import { useImpersonation } from "@/hooks/useImpersonation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type AuthContextType = ReturnType<typeof useAuthentication> & 
  Pick<ReturnType<typeof useImpersonation>, 'isImpersonating' | 'originalUser' | 'impersonateUser' | 'stopImpersonating'>;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const auth = useAuthentication();
  const impersonation = useImpersonation();

  // Function to send account creation notification
  const sendAccountCreationNotification = async (email: string) => {
    try {
      if (!email) return;
      
      await supabase.functions.invoke('send-event-notification', {
        body: {
          eventName: 'account_created',
          userEmail: email,
          dataFields: {
            creationDate: new Date().toISOString()
          }
        }
      });
    } catch (error) {
      console.error("Failed to send account creation notification:", error);
    }
  };
  
  // Function to send trial started notification
  const sendTrialStartedNotification = async (email: string) => {
    try {
      if (!email) return;
      
      await supabase.functions.invoke('send-event-notification', {
        body: {
          eventName: 'trial_started',
          userEmail: email,
          dataFields: {
            startDate: new Date().toISOString(),
            trialLength: "7 days" // Adjust as needed
          }
        }
      });
    } catch (error) {
      console.error("Failed to send trial started notification:", error);
    }
  };

  useEffect(() => {
    // Initialize impersonation state first
    impersonation.initializeImpersonation();
    
    // Set up auth state listener
    const subscription = auth.setupAuthListener();
    
    // Initialize auth
    auth.initializeAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Watch for auth state changes to detect new sign-ups
  useEffect(() => {
    // Check if auth has authEvent property to avoid TypeScript errors
    if (auth.user && auth.authEvent === 'SIGNED_UP') {
      // Send account creation notification
      sendAccountCreationNotification(auth.user.email || '');
      
      // Check if they're on a trial and send that notification too
      if (auth.user.app_metadata?.trial_start_date || auth.user.app_metadata?.is_trial_user) {
        sendTrialStartedNotification(auth.user.email || '');
      }
    }
  }, [auth.user, auth.authEvent]);

  // Override the standard logout with impersonation-aware version
  const logout = async () => {
    try {
      console.log("Logout initiated");
      
      if (impersonation.isImpersonating && impersonation.originalUser) {
        console.log("Stopping impersonation");
        await impersonation.stopImpersonating();
        return { success: true };
      }

      // Clear any old auth data before attempting logout
      sessionStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('impersonation');
      
      try {
        console.log("Executing standard logout");
        const result = await auth.logout();
        return result;
      } catch (error) {
        console.error("Enhanced logout error:", error);
        
        // Try to sign out directly as a fallback
        try {
          console.log("Attempting fallback logout");
          await supabase.auth.signOut({ scope: 'global' });
          toast.success("Successfully signed out");
          window.location.href = '/login';
          return { success: true };
        } catch (innerError) {
          console.error("Fallback logout error:", innerError);
          toast.error("Failed to sign out, please try refreshing the page");
          throw error;
        }
      }
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Failed to sign out, please try refreshing the page");
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...auth,
        logout, // Override with the impersonation-aware version
        isImpersonating: impersonation.isImpersonating,
        originalUser: impersonation.originalUser,
        impersonateUser: impersonation.impersonateUser,
        stopImpersonating: impersonation.stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
